import React, { useState } from 'react';
import {
  CARD_W, POLL_OUTCOME_H,
  POLL_AUDIO_FILE_H, POLL_AUDIO_FILE_GAP, POLL_AUDIO_EMPTY_H, POLL_AUDIO_ADD_BTN_H,
  C, ny, getPathValue, resolveFeatureIconAndLabel, normalizeAudioField,
} from '../flowCanvas.constants';
import AudioWaveformCell from './AudioWaveformCell';
import audioLoopIcon from '../../v2/CustomisationWorkspace/WorkspaceAssets/ModuleIcons/audio_loop.png';

const BORDER_R      = 8;
const HEADER_H      = 32;
const SECTION_PAD   = 10;
const DISRUPTOR_ROW_H   = 54;
const DISRUPTOR_ROW_GAP = 8;
const AUDIO_WAVE_H  = 26;
const PER_POLL_TITLE_H  = 22; // title bar height per poll slot
const PER_POLL_GAP      = 8;  // gap between consecutive poll slots

const POLL_TYPE_STYLES = {
  opinion: {
    color: '#38bdf8',
    icon: '●',
  },
  standard: {
    color: '#38bdf8',
    icon: '●',
  },
  predictive: {
    color: '#f59e0b',
    icon: '▲',
  },
  removal: {
    color: '#f97316',
    icon: '✕',
  },
  default: {
    color: C.purple,
    icon: '●',
  },
};

/**
 * PollOutcomeNode  — compound card used for vote outcome columns and
 * show/hide-previous-result columns.
 *
 * Each node renders:
 *   • a coloured title bar (using meta.sectionTitle)
 *   • a disruptor section (resolves from meta.disruptorLink dot-path)
 *   • an audio section    (resolves from meta.audioTabKey/audioGroupKey/audioVarName)
 *
 * Props:
 *   step                  – the flow step object
 *   stepX                 – absolute SVG x (centre of card)
 *   centreY               – canvas centreY
 *   isSelected            – boolean
 *   liveCustomisationData – current live data object
 *   campaignFeatures      – array of campaign feature instances (for label resolution)
 *   onActivate            – called when user clicks the card body / audio section
 *   onDisruptorActivate   – called when user clicks the disruptor slot (optional)
 */
export default function PollOutcomeNode({
  step,
  stepX,
  centreY,
  overrideY,
  isSelected,
  isDragOver,
  liveCustomisationData,
  campaignFeatures,
  activeAudioSlotVarName,
  allPolls,
  onActivate,
  onActivateAudio,
  onRemoveAudio,
  onDisruptorActivate,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveDisruptor,
  onPollTriggerDrop,
  onPollTriggerRemove,
}) {
  const stepY = overrideY ?? ny(step, centreY);

  const [dragOverPollIdx, setDragOverPollIdx] = useState(null);

  const meta         = step.customisationConfig?.meta || {};
  const sectionTitle = meta.sectionTitle || step.stepTitle;
  const pollTypeKey = (() => {
    if (meta.pollConditionType) return String(meta.pollConditionType).toLowerCase();
    const title = String(sectionTitle || step.stepTitle || '').toLowerCase();
    if (title.includes('predictive')) return 'predictive';
    if (title.includes('removal')) return 'removal';
    if (title.includes('standard') || title.includes('opinion')) return 'standard';
    return 'default';
  })();
  const pollTypeStyle = POLL_TYPE_STYLES[pollTypeKey] || POLL_TYPE_STYLES.default;
  const disruptorLink = meta.disruptorLink;     // dot-path to the disruptor id array
  const hideDisruptorSection = meta.hideDisruptorSection === true;
  const disruptorSectionLabel = meta.disruptorSectionLabel || 'DISRUPTOR';
  const disruptorAllowMultiple = meta.allowMultiple ?? step.customisationConfig?.variables?.[0]?.allowMultiple ?? true;
  const audioTabKey  = meta.audioTabKey;
  const audioGroupKey = meta.audioGroupKey;
  const audioVarName = meta.audioVarName;
  const audioToggleVarName = meta.audioToggleVarName || (audioVarName?.endsWith('Audio') ? audioVarName.slice(0, -5) : audioVarName);
  const audioAllowMultiple = meta.audioAllowMultiple ?? true;
  const audioLabel   = meta.audioLabel || 'Audio';

  const borderCol = isSelected ? C.purple : C.border;
  const audioGroup = audioTabKey && audioGroupKey
    ? liveCustomisationData?.[audioTabKey]?.[audioGroupKey]
    : null;

  // ── Resolve disruptors ───────────────────────────────────────────────────
  const disruptorItems = (() => {
    if (!disruptorLink) return [];
    const parts = disruptorLink.split('.');
    const valueKey = parts[parts.length - 1];
    const groupKeys = parts.slice(0, -1);
    const liveGroup = liveCustomisationData ? getPathValue(liveCustomisationData, groupKeys) : null;
    const fallbackVals = step.populatedData?.variables?.[0]?.allValues || [];
    const sourceVals = Array.isArray(liveGroup) ? liveGroup : fallbackVals;

    return (Array.isArray(sourceVals) ? sourceVals : [])
      .map((v, idx) => {
        const id = (v && typeof v === 'object') ? v[valueKey] : null;
        return { idx, id };
      })
      .filter(item => item.id !== null && item.id !== undefined && String(item.id).trim().length > 0);
  })();
  const disruptorIds = disruptorItems.map(item => item.id);
  const disruptorRowsCount = Math.max(disruptorItems.length, 1);
  const disruptorSlotH = disruptorRowsCount * DISRUPTOR_ROW_H + Math.max(disruptorRowsCount - 1, 0) * DISRUPTOR_ROW_GAP;

  // ── Resolve audio configured flag ─────────────────────────────────────────
  const audioItems = Array.isArray(audioGroup?.[audioVarName])
    ? audioGroup[audioVarName].filter(item => item != null)
    : [];
  const toggleObj = audioGroup?.toggleSFX?.[0];
  const hasAudioToggle = !!(toggleObj && audioToggleVarName && Object.prototype.hasOwnProperty.call(toggleObj, audioToggleVarName));
  const audioToggleEnabled = hasAudioToggle ? toggleObj[audioToggleVarName] === true : true;
  const hasConfiguredAudio = (item) => !!(item?.audioFilename || item?.filename || item?.srcUrl);
  const configuredAudioItems = audioItems.filter(hasConfiguredAudio);
  const singleDisabledAudio = configuredAudioItems.length === 1 && configuredAudioItems[0]?.playSFX === false;
  const showAudioCTA = !audioToggleEnabled || configuredAudioItems.length === 0 || singleDisabledAudio;
  const audioConfigured = !showAudioCTA;
  const isActiveAudio = activeAudioSlotVarName === audioVarName;
  const audioSectionH = audioConfigured
    ? configuredAudioItems.length * POLL_AUDIO_FILE_H
      + (configuredAudioItems.length - 1) * POLL_AUDIO_FILE_GAP
      + (audioAllowMultiple ? POLL_AUDIO_FILE_GAP + POLL_AUDIO_ADD_BTN_H : 0)
    : POLL_AUDIO_EMPTY_H;

  // ── Per-poll trigger slots (predictive / removal outcome nodes) ─────────
  const usePerPollSlots = meta.usePerPollSlots === true;
  const triggerField = pollTypeKey === 'predictive' ? 'correctDisruptor' : 'answersHiddenDisruptor';
  const matchingPolls = usePerPollSlots && Array.isArray(allPolls)
    ? allPolls
        .map((p, idx) => ({ poll: p, idx }))
        .filter(({ poll }) => (poll?.type || '').toLowerCase() === pollTypeKey)
    : [];
  const getPerPollTriggerItems = (poll, field) => {
    const raw = poll?.[field];
    if (typeof raw === 'string' && raw.trim() !== '') return [{ idx: 0, id: raw }];
    const arr = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
    return arr
      .map((item, i) => {
        const id = item && typeof item === 'object' ? item.triggerDisruptorFeatureID : item;
        return { idx: i, id };
      })
      .filter(item => item.id !== null && item.id !== undefined && String(item.id).trim() !== '');
  };
  // Height of the per-poll area (label 18px excluded — counted in contentH directly)
  const perPollSlotsH = usePerPollSlots
    ? (matchingPolls.length > 0
        ? matchingPolls.length * (PER_POLL_TITLE_H + DISRUPTOR_ROW_H) + Math.max(0, matchingPolls.length - 1) * PER_POLL_GAP
        : DISRUPTOR_ROW_H)
    : 0;

  const contentH = usePerPollSlots
    ? (HEADER_H + SECTION_PAD + 18 + perPollSlotsH + SECTION_PAD + 1 + SECTION_PAD + 18 + audioSectionH + 16)
    : (hideDisruptorSection
        ? (HEADER_H + SECTION_PAD + 18 + audioSectionH + 16)
        : (HEADER_H + SECTION_PAD + 18 + disruptorSlotH + SECTION_PAD + 1 + SECTION_PAD + 18 + audioSectionH + 16));
  const totalH = Math.max(POLL_OUTCOME_H, contentH);

  const x = stepX - CARD_W / 2;
  const y = stepY - totalH / 2;
  const slotX = x + SECTION_PAD;
  const slotW = CARD_W - SECTION_PAD * 2;

  // Layout sections inside the card
  const headerY = y;
  const disruptorLabelY = headerY + HEADER_H + SECTION_PAD;
  const slotY           = disruptorLabelY + 18;
  const dividerY        = (usePerPollSlots || !hideDisruptorSection)
    ? (slotY + (usePerPollSlots ? perPollSlotsH : disruptorSlotH) + SECTION_PAD)
    : (headerY + HEADER_H + SECTION_PAD);
  const audioLabelY     = (usePerPollSlots || !hideDisruptorSection)
    ? (dividerY + SECTION_PAD)
    : (headerY + HEADER_H + SECTION_PAD);
  const audioContentY   = audioLabelY + 18;

  return (
    <g className="fc-node" onClick={onActivate} style={{ cursor: 'pointer' }} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {/* Main card background */}
      <rect
        x={x} y={y}
        width={CARD_W} height={totalH}
        rx={BORDER_R} ry={BORDER_R}
        fill={C.card} stroke={borderCol} strokeWidth={1.5}
      />

      {/* Title header band */}
      <clipPath id={`po-hdr-${step.stepId}`}>
        <rect x={x} y={headerY} width={CARD_W} height={HEADER_H} rx={BORDER_R} ry={BORDER_R} />
      </clipPath>
      <rect
        x={x} y={headerY}
        width={CARD_W} height={HEADER_H + BORDER_R}
        clipPath={`url(#po-hdr-${step.stepId})`}
        fill="rgba(145,70,255,0.22)"
      />
      <circle
        cx={x + 20}
        cy={headerY + HEADER_H / 2}
        r={8}
        fill={pollTypeStyle.color}
        fillOpacity={0.22}
        stroke={pollTypeStyle.color}
        strokeWidth={1.2}
      />
      <text
        x={x + 20}
        y={headerY + HEADER_H / 2 + 3.5}
        fontSize="8.5"
        fontWeight="700"
        fill={pollTypeStyle.color}
        textAnchor="middle"
        style={{ userSelect: 'none' }}>
        {pollTypeStyle.icon}
      </text>
      <text
        x={x + 34} y={headerY + HEADER_H / 2 + 5}
        fontSize="11" fontWeight="700" fill={C.text}
        textAnchor="start" style={{ userSelect: 'none' }}>
        {sectionTitle}
      </text>

      {/* ── Per-poll trigger slots ────────────────────────────────────── */}
      {usePerPollSlots && (
        <>
          <text
            x={x + SECTION_PAD} y={disruptorLabelY + 12}
            fontSize="9.5" fontWeight="600" fill={C.textMuted}
            style={{ userSelect: 'none' }}>
            {disruptorSectionLabel.toUpperCase()}
          </text>

          {matchingPolls.length === 0 ? (
            <g>
              <rect
                x={slotX} y={slotY} width={slotW} height={DISRUPTOR_ROW_H}
                rx={8} ry={8}
                fill="rgba(255,255,255,0.03)" stroke={C.border} strokeWidth={1} strokeDasharray="4 3"
              />
              <text
                x={slotX + slotW / 2} y={slotY + DISRUPTOR_ROW_H / 2 + 4}
                fontSize="9.5" fill={C.textDim} textAnchor="middle"
                style={{ userSelect: 'none', pointerEvents: 'none' }}>
                No {pollTypeKey} polls configured
              </text>
            </g>
          ) : matchingPolls.map(({ poll: p, idx: pollArrayIdx }, i) => {
            const pollTitle = p?.title || `Poll ${pollArrayIdx + 1}`;
            const truncTitle = pollTitle.length > 26 ? pollTitle.slice(0, 24) + '\u2026' : pollTitle;
            const triggerItems = getPerPollTriggerItems(p, triggerField);
            const firstTrigger = triggerItems[0] || null;
            const isFocused = dragOverPollIdx === pollArrayIdx;
            const itemY   = slotY + i * (PER_POLL_TITLE_H + DISRUPTOR_ROW_H + PER_POLL_GAP);
            const slotItemY = itemY + PER_POLL_TITLE_H;
            const slotMidY  = slotItemY + DISRUPTOR_ROW_H / 2;
            const R = 6;
            const totalSlotH = PER_POLL_TITLE_H + DISRUPTOR_ROW_H;
            const slotFill = firstTrigger ? C.circle : (isFocused ? 'rgba(167,139,250,0.10)' : C.circle);
            const slotStroke = firstTrigger ? C.purple : (isFocused ? '#a78bfa' : (C.border));
            const slotStrokeW = isFocused ? 1.4 : (firstTrigger ? 1.2 : 1);
            const slotDash = firstTrigger ? undefined : '4 3';
            return (
              <g key={`per-poll-${pollArrayIdx}`}>
                {/* Unified outer container — one rounded rect for title + slot */}
                <rect
                  x={slotX} y={itemY} width={slotW} height={totalSlotH}
                  rx={R} ry={R}
                  fill={slotFill}
                  stroke={slotStroke} strokeWidth={slotStrokeW}
                  strokeDasharray={slotDash}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverPollIdx(pollArrayIdx); }}
                  onDragLeave={(e) => { e.stopPropagation(); setDragOverPollIdx(cur => cur === pollArrayIdx ? null : cur); }}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverPollIdx(null); onPollTriggerDrop?.(pollArrayIdx, triggerField, e); }}
                />
                {/* Title section: rounded-top, flat-bottom path overlaid on the container */}
                <path
                  d={`M ${slotX + R},${itemY} H ${slotX + slotW - R} Q ${slotX + slotW},${itemY} ${slotX + slotW},${itemY + R} V ${slotItemY} H ${slotX} V ${itemY + R} Q ${slotX},${itemY} ${slotX + R},${itemY} Z`}
                  fill={`${pollTypeStyle.color}28`}
                  style={{ pointerEvents: 'none' }}
                />
                {/* Left accent stripe */}
                <rect
                  x={slotX} y={itemY} width={3} height={PER_POLL_TITLE_H}
                  rx={2} ry={2}
                  fill={pollTypeStyle.color}
                  style={{ pointerEvents: 'none' }}
                />
                {/* Divider line separating title from slot body */}
                <line
                  x1={slotX} y1={slotItemY} x2={slotX + slotW} y2={slotItemY}
                  stroke={slotStroke} strokeWidth={0.7} strokeOpacity={0.5}
                />
                <text
                  x={slotX + 12} y={itemY + PER_POLL_TITLE_H / 2 + 4}
                  fontSize="9.5" fontWeight="700" fill={pollTypeStyle.color}
                  style={{ userSelect: 'none', pointerEvents: 'none' }}>
                  {truncTitle}
                </text>

                {/* Drop slot content (background/border already on outer container) */}
                {firstTrigger ? (
                  <g style={{ pointerEvents: 'none' }}>
                    <circle
                      cx={slotX + 24} cy={slotMidY} r={16}
                      fill="rgba(145,70,255,0.15)" stroke="rgba(145,70,255,0.35)" strokeWidth="1"
                    />
                    <text x={slotX + 24} y={slotMidY + 5} textAnchor="middle" fontSize="14">
                      {resolveFeatureIconAndLabel(firstTrigger.id, campaignFeatures).icon}
                    </text>
                    <text x={slotX + 48} y={slotMidY + 4} fontSize="9.5" fontWeight="700" fill={C.text}>
                      {(() => { const l = resolveFeatureIconAndLabel(firstTrigger.id, campaignFeatures).label; return l.length > 22 ? l.slice(0, 22) + '\u2026' : l; })()}
                    </text>
                  </g>
                ) : (
                  <text
                    x={slotX + slotW / 2} y={slotItemY + DISRUPTOR_ROW_H / 2 + 4}
                    fontSize="9.5" fill={isFocused ? C.textMuted : C.textDim} textAnchor="middle"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}>
                    Drag a component here
                  </text>
                )}
                {/* Remove button (rendered on top, needs pointer events) */}
                {firstTrigger && onPollTriggerRemove && (
                  <g
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); onPollTriggerRemove(pollArrayIdx, triggerField, firstTrigger.idx); }}
                  >
                    <circle cx={slotX + slotW - 14} cy={slotItemY + 14} r={10} fill="#dc2626" />
                    <g transform={`translate(${slotX + slotW - 14}, ${slotItemY + 14})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                      <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                      <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                      <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                      <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                    </g>
                  </g>
                )}
              </g>
            );
          })}
        </>
      )}

      {/* ── Disruptor section ──────────────────────────────────────────── */}
      {!hideDisruptorSection && (
        <>
          <text
            x={x + SECTION_PAD} y={disruptorLabelY + 12}
            fontSize="9.5" fontWeight="600" fill={C.textMuted}
            style={{ userSelect: 'none' }}>{disruptorSectionLabel.toUpperCase()}</text>

          {/* Disruptor slot — clickable to open the disruptor config */}
          {disruptorIds.length === 0 && (
            <rect
              x={slotX} y={slotY}
              width={slotW} height={disruptorSlotH}
              rx={8} ry={8}
              fill={isDragOver ? 'rgba(167,139,250,0.10)' : C.circle}
              stroke={isDragOver ? '#a78bfa' : C.border}
              strokeWidth={isDragOver ? 1.4 : 1}
              strokeDasharray="4 3"
              style={onDisruptorActivate ? { cursor: 'pointer' } : {}}
              onClick={(e) => { if (onDisruptorActivate) { e.stopPropagation(); onDisruptorActivate(); } }}
            />
          )}

          {disruptorIds.length > 0 ? (
            disruptorItems.map((item, idx) => {
              const rowY = slotY + idx * (DISRUPTOR_ROW_H + DISRUPTOR_ROW_GAP);
              const midY = rowY + DISRUPTOR_ROW_H / 2;
              const { label, icon } = resolveFeatureIconAndLabel(item.id, campaignFeatures);
              const displayLabel = label.length > 22 ? label.slice(0, 22) + '…' : label;
              return (
                <g
                  key={`${step.stepId}-disruptor-${item.idx}-${idx}`}
                  style={onDisruptorActivate ? { cursor: 'pointer' } : {}}
                  onClick={(e) => { if (onDisruptorActivate) { e.stopPropagation(); onDisruptorActivate(); } }}
                >
                  <rect
                    x={slotX}
                    y={rowY + 2}
                    width={slotW}
                    height={DISRUPTOR_ROW_H - 4}
                    rx={8}
                    ry={8}
                    fill={C.circle}
                    stroke={C.purple}
                    strokeWidth={1.2}
                  />
                  <circle
                    cx={slotX + 24}
                    cy={midY}
                    r={16}
                    fill="rgba(145,70,255,0.15)"
                    stroke="rgba(145,70,255,0.35)"
                    strokeWidth="1"
                    style={{ pointerEvents: 'none' }}
                  />
                  <text
                    x={slotX + 24}
                    y={midY + 5}
                    textAnchor="middle"
                    fontSize="14"
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                  >{icon}</text>
                  <text
                    x={slotX + 48}
                    y={midY + 4}
                    fontSize="9.5"
                    fontWeight="700"
                    fill={C.text}
                    style={{ userSelect: 'none', pointerEvents: 'none' }}
                  >
                    {displayLabel}
                  </text>
                  {onRemoveDisruptor && (
                    <g
                      onClick={(e) => { e.stopPropagation(); onRemoveDisruptor(item.idx); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <circle cx={slotX + slotW - 14} cy={rowY + 14} r={10} fill="#dc2626" />
                      <g transform={`translate(${slotX + slotW - 14}, ${rowY + 14})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                        <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                        <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                        <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                        <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                      </g>
                    </g>
                  )}
                </g>
              );
            })
          ) : (
            <text
              x={x + CARD_W / 2} y={slotY + disruptorSlotH / 2 + 4}
              fontSize="10" fill={C.textDim}
              textAnchor="middle" style={{ userSelect: 'none', pointerEvents: 'none' }}>
              {disruptorAllowMultiple ? 'Drag components here (multiple)' : 'Drag a component here'}
            </text>
          )}
          {/* Transparent overlay to capture empty-state clicks */}
          {onDisruptorActivate && disruptorIds.length === 0 && (
            <rect
              x={slotX} y={disruptorLabelY}
              width={slotW} height={disruptorSlotH + 24}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); onDisruptorActivate(); }}
            />
          )}
        </>
      )}

      {/* Divider */}
      {(!hideDisruptorSection || usePerPollSlots) && (
        <line
          x1={x + SECTION_PAD} y1={dividerY}
          x2={x + CARD_W - SECTION_PAD} y2={dividerY}
          stroke={C.border} strokeWidth={1}
        />
      )}

      {/* ── Audio section ─────────────────────────────────────────────── */}
      <text
        x={x + SECTION_PAD} y={audioLabelY + 12}
        fontSize="9.5" fontWeight="600" fill={C.textMuted}
        style={{ userSelect: 'none' }}>AUDIO — {audioLabel.toUpperCase()}</text>

      {audioConfigured ? (
        <>
          {configuredAudioItems.map((item, fileIndex) => {
            const rowY = audioContentY + fileIndex * (POLL_AUDIO_FILE_H + POLL_AUDIO_FILE_GAP);
            const itemDisabled = item?.playSFX === false || (hasAudioToggle && !audioToggleEnabled);
            const rawAudioFilename = item?.audioFilename || item?.filename;
            const { name: resolvedAudioName, srcUrl: audioSrcUrl } = normalizeAudioField(rawAudioFilename);
            const audioName = resolvedAudioName || 'audio.ogg';
            const audioShortName = audioName.length > 21 ? `${audioName.slice(0, 19)}…` : audioName;
            const audioVolume = item?.volume !== undefined ? item.volume : 100;
            const isLooping = item?.loop === true;
            const volumeBadgeY = rowY + POLL_AUDIO_FILE_H - 21;
            const volumeBadgeW = 54;
            const volumeBadgeX = x + CARD_W - SECTION_PAD - 8 - volumeBadgeW;
            const loopIconSize = 18;
            const loopLabel = 'Looped';
            const loopLabelW = 34;
            const loopIconX = volumeBadgeX - loopIconSize - loopLabelW - 8;
            const fileLabelX = x + SECTION_PAD + 28;
            return (
              <g
                key={`${audioVarName || 'audio'}-${fileIndex}`}
                style={{ cursor: 'pointer', opacity: itemDisabled ? 0.38 : 1 }}
                onClick={(e) => { e.stopPropagation(); onActivateAudio?.({ slotVarName: audioVarName, fileIndex }); }}
              >
                <rect
                  x={x + SECTION_PAD} y={rowY}
                  width={CARD_W - SECTION_PAD * 2} height={POLL_AUDIO_FILE_H}
                  rx={5} ry={5}
                  fill={isActiveAudio && !itemDisabled ? 'rgba(74,222,128,0.12)' : itemDisabled ? 'rgba(145,70,255,0.04)' : 'rgba(145,70,255,0.09)'}
                  stroke={isActiveAudio && !itemDisabled ? 'rgba(74,222,128,0.75)' : itemDisabled ? 'rgba(145,70,255,0.15)' : 'rgba(145,70,255,0.28)'}
                  strokeWidth={isActiveAudio && !itemDisabled ? 1.5 : 1}
                  strokeDasharray={itemDisabled ? '4 3' : undefined}
                />
                <text x={x + SECTION_PAD + 10} y={rowY + 19}
                  fontSize="14" fill={isActiveAudio && !itemDisabled ? C.greenText : C.purple} style={{ userSelect: 'none' }}>♪</text>
                <text x={fileLabelX} y={rowY + 19}
                  fontSize="10.5" fontWeight="600" fill={C.text}
                  style={{ userSelect: 'none' }}>{audioShortName}</text>
                {!itemDisabled && (
                  <>
                    {isLooping && (
                      <>
                        <image
                          href={audioLoopIcon}
                          x={loopIconX}
                          y={volumeBadgeY}
                          width={loopIconSize}
                          height={loopIconSize}
                          style={{ filter: 'brightness(0) saturate(100%) invert(71%) sepia(39%) saturate(642%) hue-rotate(90deg) brightness(94%) contrast(89%)' }}
                        />
                        <text
                          x={loopIconX + loopIconSize + 4}
                          y={volumeBadgeY + 12}
                          fontSize="9"
                          fill={C.greenText}
                          style={{ userSelect: 'none', fontWeight: 600 }}
                        >{loopLabel}</text>
                      </>
                    )}
                    <rect x={volumeBadgeX} y={volumeBadgeY}
                      width={volumeBadgeW} height={18} rx={5} ry={5}
                      fill="rgba(255,255,255,0.07)" />
                    <text x={volumeBadgeX + volumeBadgeW / 2} y={volumeBadgeY + 13}
                      fontSize="9" fill={C.textMuted} textAnchor="middle"
                      style={{ userSelect: 'none' }}>VOL {audioVolume}%</text>
                  </>
                )}
                {onRemoveAudio && (
                  <g
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); onRemoveAudio({ slotVarName: audioVarName, fileIndex }); }}
                  >
                    <circle cx={x + CARD_W - SECTION_PAD - 9} cy={rowY + 9} r={7} fill="#dc2626" />
                    <g transform={`translate(${x + CARD_W - SECTION_PAD - 9}, ${rowY + 9})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                      <rect x="-1.5" y="-4" width="3" height="1.5" rx="0.7" fill="#fee2e2" />
                      <rect x="-3.5" y="-2.5" width="7" height="1.2" rx="0.6" fill="#fee2e2" />
                      <rect x="-2.7" y="-1" width="5.4" height="4.8" rx="0.6" fill="#fee2e2" />
                      <rect x="-1.2" y="0" width="0.7" height="3" rx="0.35" fill="#dc2626" />
                      <rect x="0.5" y="0" width="0.7" height="3" rx="0.35" fill="#dc2626" />
                    </g>
                  </g>
                )}
                {itemDisabled ? (
                  <text x={x + SECTION_PAD + (CARD_W - SECTION_PAD * 2) / 2} y={rowY + 19}
                    fontSize="8.5" textAnchor="middle" fill={C.textMuted}
                    style={{ userSelect: 'none' }}>(disabled)</text>
                ) : (
                  <foreignObject
                    x={x + SECTION_PAD + 8} y={rowY + 27}
                    width={CARD_W - SECTION_PAD * 2 - 16} height={AUDIO_WAVE_H}
                  >
                    <AudioWaveformCell srcUrl={audioSrcUrl} height={AUDIO_WAVE_H} />
                  </foreignObject>
                )}
                {itemDisabled && (
                  <text x={x + SECTION_PAD + 10} y={rowY + 60}
                    fontSize="8.5" fill={C.textMuted}
                    style={{ userSelect: 'none' }}>
                    Audio disabled
                  </text>
                )}
              </g>
            );
          })}
          {audioAllowMultiple && (
            <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onActivateAudio?.({ slotVarName: audioVarName, fileIndex: null }); }}>
              <rect x={x + SECTION_PAD} y={audioContentY + configuredAudioItems.length * (POLL_AUDIO_FILE_H + POLL_AUDIO_FILE_GAP)}
                width={CARD_W - SECTION_PAD * 2} height={POLL_AUDIO_ADD_BTN_H}
                rx={5} ry={5}
                fill="none"
                stroke="rgba(145,70,255,0.35)" strokeWidth={1} strokeDasharray="4 3"
              />
              <text x={x + CARD_W / 2} y={audioContentY + configuredAudioItems.length * (POLL_AUDIO_FILE_H + POLL_AUDIO_FILE_GAP) + POLL_AUDIO_ADD_BTN_H / 2 + 4}
                fontSize="9.5" textAnchor="middle" fill={C.purple}
                style={{ userSelect: 'none' }}>＋ Add another file</text>
            </g>
          )}
        </>
      ) : (
        <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onActivateAudio?.({ slotVarName: audioVarName, fileIndex: singleDisabledAudio ? 0 : null }); }}>
          <rect
            x={x + SECTION_PAD} y={audioContentY}
            width={CARD_W - SECTION_PAD * 2} height={POLL_AUDIO_EMPTY_H}
            rx={5} ry={5}
            fill={isActiveAudio ? 'rgba(74,222,128,0.10)' : 'none'}
            stroke={isActiveAudio ? 'rgba(74,222,128,0.6)' : C.textDim}
            strokeWidth={isActiveAudio ? 1.5 : 1}
            strokeDasharray="4 3"
          />
          <text x={x + CARD_W / 2} y={audioContentY + POLL_AUDIO_EMPTY_H / 2 - 4}
            fontSize="16" textAnchor="middle" fill={isActiveAudio ? C.greenText : C.textDim}
            style={{ userSelect: 'none' }}>+</text>
          <text x={x + CARD_W / 2} y={audioContentY + POLL_AUDIO_EMPTY_H / 2 + 11}
            fontSize="9.5" textAnchor="middle" fill={isActiveAudio ? C.greenText : C.textDim}
            style={{ userSelect: 'none' }}>Click to add audio</text>
        </g>
      )}
    </g>
  );
}
